import { useEffect, useState } from 'react';

// env-kit — client side. <DevEnvBanner /> renders nothing on prod; on dev a bar
// pinned to the bottom of every screen (login included) says this is a test
// environment and links to the real app. Self-styled (design-kit tokens with
// fallbacks), so it works in any app. While it is shown, <html> gets the class
// `has-dev-env-banner` and the CSS variable `--dev-env-banner-h` (its height),
// so the app can keep its own content clear of it; by default the body gets
// that much bottom padding.

export interface EnvironmentInfo { deployEnv: 'dev' | 'prod'; prodAppUrl: string | null }

export interface DevEnvBannerLabels {
  label: string;   // "Environnement DEV"
  text: string;    // "Environnement de test — … Pour le travail réel, utilisez :"
}

const DEFAULT_LABELS: DevEnvBannerLabels = {
  label: 'Environnement DEV',
  text: 'Environnement de test — les modifications faites ici ne sont pas reprises dans l’application et peuvent être écrasées à tout moment. Pour le travail réel, utilisez :',
};

const STYLE_ID = 'env-kit-style';
const CSS = `
:root { --dev-env-banner-h: 48px; }
@media (max-width: 720px) { :root { --dev-env-banner-h: 88px; } }
html.has-dev-env-banner body { padding-bottom: var(--dev-env-banner-h); }
.dev-env-banner {
  position: fixed; left: 0; right: 0; bottom: 0; z-index: 1000;
  height: var(--dev-env-banner-h); box-sizing: border-box;
  display: flex; flex-wrap: wrap; align-items: center; justify-content: center;
  gap: 0 var(--s8, 8px); padding: 0 var(--s16, 16px);
  background: var(--warn, #c08820); color: #fff;
  font-family: var(--font, inherit); font-size: var(--t-13, 13px); line-height: 1.3; text-align: center;
}
.dev-env-banner strong { text-transform: uppercase; letter-spacing: 0.04em; }
.dev-env-banner a { color: #fff; font-weight: 600; text-decoration: underline; }
`;

function ensureStyle() {
  if (typeof document === 'undefined' || document.getElementById(STYLE_ID)) return;
  const el = document.createElement('style');
  el.id = STYLE_ID;
  el.textContent = CSS;
  document.head.appendChild(el);
}

/** Reads the app's /api/environment (env-kit/server environmentRouter). */
export function useEnvironment(endpoint = '/api/environment'): EnvironmentInfo | null {
  const [env, setEnv] = useState<EnvironmentInfo | null>(null);
  useEffect(() => {
    let cancelled = false;
    fetch(endpoint)
      .then((r) => (r.ok ? r.json() : null))
      .then((e) => { if (!cancelled) setEnv(e); })
      .catch(() => { /* no banner */ });
    return () => { cancelled = true; };
  }, [endpoint]);
  return env;
}

export function DevEnvBanner({ labels, endpoint, prodAppUrl }: {
  labels?: Partial<DevEnvBannerLabels>;
  /** Default '/api/environment'. */
  endpoint?: string;
  /** Overrides the server's prodAppUrl. */
  prodAppUrl?: string;
}) {
  const env = useEnvironment(endpoint);
  const isDev = env?.deployEnv === 'dev';

  useEffect(() => {
    if (!isDev) return;
    ensureStyle();
    document.documentElement.classList.add('has-dev-env-banner');
    return () => document.documentElement.classList.remove('has-dev-env-banner');
  }, [isDev]);

  if (!isDev) return null;
  const L = { ...DEFAULT_LABELS, ...labels };
  const url = prodAppUrl ?? env?.prodAppUrl ?? null;
  return (
    <div className="dev-env-banner" role="status">
      <strong>{L.label}</strong>
      <span>{L.text}</span>
      {url && <a href={url}>{url.replace(/^https?:\/\//, '')}</a>}
    </div>
  );
}
